import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Users, Plus, BadgeCheck } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { agencyMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { StatusPill, LoadingScreen, EmptyState } from "@/components/common";

const G_STATUS = ["ACTIVE", "ON_LEAVE", "ABSENT", "REPLACEMENT_REQUIRED", "INACTIVE"];

export default function Guards() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", gender: "Male", armed: false, experience_years: 1, assigned_location: "", shift: "Day (8AM-8PM)", training_status: "Completed", id_verified: true, status: "ACTIVE", joining_date: "" });

  const load = () => api.get("/guards").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!f.name) return toast.error("Guard name required");
    try { await api.post("/guards", { ...f, experience_years: Number(f.experience_years), armed: !!f.armed }); toast.success("Guard added"); setOpen(false); setF({ ...f, name: "", assigned_location: "" }); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const changeStatus = async (g, status) => {
    try { await api.put(`/guards/${g.id}`, { ...g, status }); load(); } catch (e) { toast.error("Failed"); }
  };

  return (
    <DashboardShell menu={agencyMenu} title="Guard Management">
      <PageHeader title="Security Guards" subtitle="Manage your workforce, verification and deployment."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-brand hover:bg-brand-600" data-testid="add-guard-btn"><Plus size={16} className="mr-1" /> Add Guard</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Add Security Guard</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="text-xs">Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="g-name" /></div>
                <div><Label className="text-xs">Gender</Label>
                  <Select value={f.gender} onValueChange={(v) => setF({ ...f, gender: v })}><SelectTrigger data-testid="g-gender"><SelectValue /></SelectTrigger><SelectContent>{["Male", "Female"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="text-xs">Experience (years)</Label><Input type="number" value={f.experience_years} onChange={(e) => setF({ ...f, experience_years: e.target.value })} data-testid="g-exp" /></div>
                <div><Label className="text-xs">Assigned Location</Label><Input value={f.assigned_location} onChange={(e) => setF({ ...f, assigned_location: e.target.value })} data-testid="g-location" /></div>
                <div><Label className="text-xs">Shift</Label>
                  <Select value={f.shift} onValueChange={(v) => setF({ ...f, shift: v })}><SelectTrigger data-testid="g-shift"><SelectValue /></SelectTrigger><SelectContent>{["Day (8AM-8PM)", "Night (8PM-8AM)"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <DialogFooter><Button className="bg-brand hover:bg-brand-600" onClick={submit} data-testid="submit-guard-btn">Add Guard</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />
      {!rows ? <LoadingScreen /> : rows.length === 0 ? (
        <EmptyState icon={Users} title="No guards added" hint="Add your security guards to manage attendance and deployment." />
      ) : (
        <div className="bg-white border border-navy-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-navy-50 text-navy-500 text-xs uppercase"><tr><th className="text-left p-3">Guard</th><th className="p-3">Gender</th><th className="p-3">Exp</th><th className="p-3">Location</th><th className="p-3">Shift</th><th className="p-3">Verified</th><th className="p-3">Status</th></tr></thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.id} className="border-t border-navy-100" data-testid={`guard-${g.id}`}>
                  <td className="p-3 font-medium text-navy-900">{g.name}</td>
                  <td className="p-3 text-center text-navy-600">{g.gender}</td>
                  <td className="p-3 text-center text-navy-600">{g.experience_years}y</td>
                  <td className="p-3 text-navy-600">{g.assigned_location || "—"}</td>
                  <td className="p-3 text-navy-600 text-xs">{g.shift}</td>
                  <td className="p-3 text-center">{g.id_verified ? <BadgeCheck size={16} className="text-emerald-500 mx-auto" /> : "—"}</td>
                  <td className="p-3">
                    <Select value={g.status} onValueChange={(v) => changeStatus(g, v)}>
                      <SelectTrigger className="h-8 w-40" data-testid={`guard-status-${g.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{G_STATUS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardShell>
  );
}
